import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AlertSettings = ({ isOpen, onClose, onSave, initialSettings = null }) => {
  const [settings, setSettings] = useState({
    name: initialSettings?.name || '',
    keywords: initialSettings?.keywords || '',
    markets: initialSettings?.markets || ['all'],
    impactLevel: initialSettings?.impactLevel || 'medium',
    sources: initialSettings?.sources || ['all'],
    frequency: initialSettings?.frequency || 'immediate',
    channels: initialSettings?.channels || ['email'],
    isActive: initialSettings?.isActive !== false
  });

  const marketOptions = [
    { value: 'all', label: 'All Markets' },
    { value: 'eua', label: 'EUA' },
    { value: 'oil', label: 'Oil Futures' }
  ];

  const impactOptions = [
    { value: 'high', label: 'High Impact Only (8-10)' },
    { value: 'medium', label: 'Medium+ Impact (6-10)' },
    { value: 'low', label: 'Low+ Impact (4-10)' },
    { value: 'all', label: 'All Impact Levels' }
  ];

  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    { value: 'reuters', label: 'Reuters' },
    { value: 'bloomberg', label: 'Bloomberg' },
    { value: 'wsj', label: 'Wall Street Journal' },
    { value: 'ft', label: 'Financial Times' },
    { value: 'platts', label: 'S&P Global Platts' }
  ];

  const frequencyOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'hourly', label: 'Hourly Digest' },
    { value: 'daily', label: 'Daily Summary' },
    { value: 'weekly', label: 'Weekly Report' }
  ];

  const handleSave = () => {
    if (!settings?.name?.trim()) {
      alert('Please enter an alert name');
      return;
    }
    onSave(settings);
    onClose();
  };

  const handleChannelChange = (channel, checked) => {
    setSettings(prev => ({
      ...prev,
      channels: checked 
        ? [...prev?.channels, channel]
        : prev?.channels?.filter(c => c !== channel)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={20} className="text-foreground" />
            <h2 className="text-lg font-semibold text-foreground">
              {initialSettings ? 'Edit Alert' : 'Create New Alert'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-1"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <Input
            label="Alert Name"
            type="text"
            placeholder="Enter alert name..."
            value={settings?.name}
            onChange={(e) => setSettings(prev => ({ ...prev, name: e?.target?.value }))}
            required
          />

          <Input
            label="Keywords"
            type="text"
            placeholder="Enter keywords separated by commas..."
            description="Leave empty to match all content based on other filters"
            value={settings?.keywords}
            onChange={(e) => setSettings(prev => ({ ...prev, keywords: e?.target?.value }))}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Markets"
              options={marketOptions}
              value={settings?.markets?.[0]}
              onChange={(value) => setSettings(prev => ({ ...prev, markets: [value] }))}
            />

            <Select
              label="Minimum Impact Level"
              options={impactOptions}
              value={settings?.impactLevel}
              onChange={(value) => setSettings(prev => ({ ...prev, impactLevel: value }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Sources"
              options={sourceOptions}
              value={settings?.sources?.[0]}
              onChange={(value) => setSettings(prev => ({ ...prev, sources: [value] }))}
              searchable
            />

            <Select
              label="Frequency"
              options={frequencyOptions}
              value={settings?.frequency}
              onChange={(value) => setSettings(prev => ({ ...prev, frequency: value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Notification Channels
            </label>
            <div className="space-y-3">
              <Checkbox
                label="Email notifications"
                checked={settings?.channels?.includes('email')}
                onChange={(e) => handleChannelChange('email', e?.target?.checked)}
              />
              <Checkbox
                label="In-app notifications"
                checked={settings?.channels?.includes('app')}
                onChange={(e) => handleChannelChange('app', e?.target?.checked)}
              />
              <Checkbox
                label="SMS notifications (high impact only)"
                checked={settings?.channels?.includes('sms')}
                onChange={(e) => handleChannelChange('sms', e?.target?.checked)}
              />
              <Checkbox
                label="Webhook notifications"
                checked={settings?.channels?.includes('webhook')}
                onChange={(e) => handleChannelChange('webhook', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <h4 className="font-medium text-foreground">Alert Status</h4>
              <p className="text-sm text-muted-foreground">
                Enable or disable this alert
              </p>
            </div>
            <Checkbox
              checked={settings?.isActive}
              onChange={(e) => setSettings(prev => ({ ...prev, isActive: e?.target?.checked }))}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            className="flex items-center gap-2"
          >
            <Icon name="Save" size={16} />
            <span>{initialSettings ? 'Update Alert' : 'Create Alert'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertSettings;